import React, { useState, ChangeEvent } from "react";
import styled from "styled-components";
import InputField from "components/InputField";
import { Industry, CompanySize, Position, Role, FormErrors } from "pages/auth/SignupPage/index";
import { designTokens, getTypography } from "styles/designTokens";
import { INDUSTRY_NAMES, POSITION_NAMES, ROLE_NAMES, COMPANY_SIZE_NAMES } from "constants/dropdownOptions";
import UnifiedDropdown from "components/dropdown/UnifiedDropdown";

// 옵션 배열들 (공통 상수 사용)
const industryOptions: Industry[] = INDUSTRY_NAMES;
const companySizeOptions: CompanySize[] = COMPANY_SIZE_NAMES;
const positionOptions: Position[] = POSITION_NAMES;
const roleOptions: Role[] = ROLE_NAMES;

interface ProfileStepProps {
  industry: Industry;
  setIndustry: (value: Industry) => void;
  companySize: CompanySize;
  setCompanySize: (value: CompanySize) => void;
  position: Position;
  setPosition: (value: Position) => void;
  role: Role;
  setRole: (value: Role) => void;
  industryCustom: string;
  setIndustryCustom: (value: string) => void;
  companySizeCustom: string;
  setCompanySizeCustom: (value: string) => void;
  positionCustom: string;
  setPositionCustom: (value: string) => void;
  roleCustom: string;
  setRoleCustom: (value: string) => void;
  companyName: string;
  setCompanyName: (value: string) => void;
  isPrivacyConsent: boolean;
  setIsPrivacyConsent: (value: boolean) => void;
  isMarketingConsent: boolean;
  setIsMarketingConsent: (value: boolean) => void;
  errors: FormErrors;
}
// 커스텀 스타일 컴포넌트들
const ErrorMessage = styled.div`
  margin-top: 4px;
  ${getTypography("korean", "body4")}
  color: #ff3b30;
`;

const ConsentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* ← 'start'는 shorthand가 아니므로 'flex-start'로 써야 함 */
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  gap: 8px;
  margin: 12px 0 18px 0;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    input {
      accent-color: ${props => props.theme?.colors?.primary || '#007AFF'}; /* ✅ 체크박스 색상 통일 (선택사항) */
    }

    span {
      ${getTypography("korean", "body4")}
      color: ${designTokens.colors.font.secondary};
    }
  }
`;

const ProfileStep: React.FC<ProfileStepProps> = ({
  industry,
  setIndustry,
  companySize,
  setCompanySize,
  position,
  setPosition,
  role,
  setRole,
  industryCustom,
  setIndustryCustom,
  positionCustom,
  setPositionCustom,
  roleCustom,
  setRoleCustom,
  companyName,
  setCompanyName,
  isPrivacyConsent,
  setIsPrivacyConsent,
  isMarketingConsent,
  setIsMarketingConsent,
  errors,
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  return (
    <>
      {/* 업종 (필수) */}
      <UnifiedDropdown
        label="업종"
        dropdownName="industry"
        options={industryOptions}
        selectedOption={industry}
        setSelectedOption={setIndustry}
        customValue={industryCustom}
        setCustomValue={setIndustryCustom}
        placeholder="업종을 선택해주세요"
        error={errors.industry}
        customError={errors.industryCustom}
        openDropdown={openDropdown}
        setOpenDropdown={setOpenDropdown}
      />

      {/* 회사규모 (필수) */}
      <UnifiedDropdown
        label="회사규모"
        dropdownName="companySize"
        options={companySizeOptions}
        selectedOption={companySize}
        setSelectedOption={setCompanySize}
        placeholder="회사규모를 선택해주세요"
        error={errors.companySize}
        openDropdown={openDropdown}
        setOpenDropdown={setOpenDropdown}
        showCustomInput={false}
      />

      {/* 직책 (선택) */}
      <UnifiedDropdown
        label="직책"
        dropdownName="position"
        options={positionOptions}
        selectedOption={position}
        setSelectedOption={setPosition}
        customValue={positionCustom}
        setCustomValue={setPositionCustom}
        placeholder="직책을 선택해주세요 (선택)"
        error={errors.position}
        customError={errors.positionCustom}
        openDropdown={openDropdown}
        setOpenDropdown={setOpenDropdown}
      />

      {/* 직무 (선택) */}
      <UnifiedDropdown
        label="직무"
        dropdownName="role"
        options={roleOptions}
        selectedOption={role}
        setSelectedOption={setRole}
        customValue={roleCustom}
        setCustomValue={setRoleCustom}
        placeholder="직무를 선택해주세요 (선택)"
        error={errors.role}
        customError={errors.roleCustom}
        openDropdown={openDropdown}
        setOpenDropdown={setOpenDropdown}
      />

      {/* 회사명 */}
        <InputField
          label="회사명"
          type="text"
          value={companyName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setCompanyName(e.target.value)
          }
          placeholder="회사명을 입력해주세요"
        />

      {/* 동의 항목들 */}
      <ConsentWrapper>
    <label>
      <input
        type="checkbox"
        checked={isPrivacyConsent}
        onChange={(e) => setIsPrivacyConsent(e.target.checked)}
        required
      />
      <span>개인정보 처리방침에 동의합니다 (필수)</span>
    </label>
    <label>
      <input
        type="checkbox"
        checked={isMarketingConsent}
        onChange={(e) => setIsMarketingConsent(e.target.checked)}
      />
      <span>마케팅 정보 수신에 동의합니다 (선택)</span>
    </label>
</ConsentWrapper>

    </>
  );
};

export default ProfileStep;
